import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';

import i18n from 'i18n-js';
import * as Yup from 'yup';
import Box from '@material-ui/core/Box';
import { BaseModal } from 'components/UI';
import { Formik, Form, Field } from 'formik';
import { AuthActions } from 'state/modules/auth';
import { TextField } from 'components/FormFields';
import Typography from '@material-ui/core/Typography';
import { makeStyles } from '@material-ui/core/styles';
import { useDispatch, useSelector } from 'react-redux';

const useStyles = makeStyles((theme) => {
  return {
    subtitle: {
      color: theme.palette.text.secondary,
      fontSize: theme.typography.fontSize,
      fontWeight: theme.typography.fontWeightBold,
    },
    content: {
      color: theme.palette.text.secondary,
      fontSize: theme.typography.fontSize,
    },
  };
});

const ChangePasswordModal = ({ open, handleClick }) => {
  const classes = useStyles();
  const formRef = useRef(null);
  const dispatch = useDispatch();

  const { fetchingChangePassword, fetchedChangePasswordSuccess } = useSelector(
    (state) => state.auth
  );

  const ChangePasswordSchema = Yup.object().shape({
    password: Yup.string().required(i18n.t('common.required_field')),
    new_password: Yup.string()
      .required(i18n.t('common.required_field'))
      .matches(
        '^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#-._$%^&*])(?=.{8,})',
        i18n.t('account.password_matches')
      ),
  });

  useEffect(() => {
    if (fetchedChangePasswordSuccess) {
      handleClick();
    }
  }, [fetchedChangePasswordSuccess, handleClick]);

  const handleSubmit = () => {
    formRef.current.submitForm();
  };

  return (
    <BaseModal
      data-cy="change-password-modal"
      size="sm"
      open={open}
      hasEnterButton
      onEnter={handleSubmit}
      onCancel={handleClick}
      hasCancelButton={false}
      enableCloseButton={false}
      loading={fetchingChangePassword}
      title={i18n.t('account.update_password')}
      enterButtonLabel={i18n.t('account.change_password')}
    >
      <Box>
        <Typography variant="subtitle1" className={classes.subtitle} gutterBottom>
          {i18n.t('account.change_pwd_msg_1')}
        </Typography>
        <Typography variant="body1" className={classes.content} gutterBottom>
          {i18n.t('account.change_pwd_msg_2')}
        </Typography>
      </Box>
      <Box width="100%" mt={2}>
        <Formik
          initialValues={{
            password: '',
            new_password: '',
          }}
          innerRef={formRef}
          validationSchema={ChangePasswordSchema}
          onSubmit={(values) => {
            dispatch(AuthActions.fetchChangePassword(values));
          }}
        >
          {({ handleSubmit }) => (
            <Form noValidate onSubmit={handleSubmit}>
              <Box mt={1}>
                <Field
                  type="password"
                  name="password"
                  component={TextField}
                  label={i18n.t('account.current_password')}
                />
              </Box>
              <Box mt={1}>
                <Field
                  type="password"
                  name="new_password"
                  component={TextField}
                  label={i18n.t('account.new_password')}
                />
              </Box>
            </Form>
          )}
        </Formik>
      </Box>
    </BaseModal>
  );
};

ChangePasswordModal.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClick: PropTypes.func.isRequired,
};

ChangePasswordModal.defaultProps = {
  open: false,
  handleClick: () => {},
};

export default ChangePasswordModal;
